import { format, isValid, parse, parseISO } from 'date-fns';
import { t } from 'i18next';

export interface FormatterPropsBase {
  value: unknown;
  defaultValue: string;
}

export type NumberFormatterProps = FormatterPropsBase & {
  locale: Locale;
  maximumFractionDigits?: number;
  exceptionOnInvalidCast?: boolean;
  factor?: number;
};

export enum DateFormat {
  date = 'P',
  dateTime = 'P kk:mm:ss',
}

export type DateFormatterProps = FormatterPropsBase & {
  locale: Locale;
  exceptionOnInvalidCast?: boolean;
  format?: DateFormat | string;
};

export type BooleanFormatterProps = FormatterPropsBase & {
  textTrue: string;
  textFalse: string;
};

export type PercentFormatterProps = Omit<NumberFormatterProps, 'factor'>;

const isoDateRegex = /^\d{4}-\d{2}-\d{2}$/;
const isoDateTimeRegex = /^\d{4}-\d{2}-\d{2}T/;
const slashDateRegex = /^\d{2}\/\d{2}\/\d{4}$/;

const isDateOnlyFormat = (formatDate: DateFormat | string) => !/[HhKkmsSaAxXzZO]/.test(formatDate);

const parseDateFromString = (value: string, formatDate: DateFormat | string) => {
  if (isoDateRegex.test(value)) {
    return parse(value, 'yyyy-MM-dd', new Date());
  }

  if (slashDateRegex.test(value)) {
    return parse(value, 'dd/MM/yyyy', new Date());
  }

  if (isDateOnlyFormat(formatDate) && isoDateTimeRegex.test(value)) {
    return parse(value.slice(0, 10), 'yyyy-MM-dd', new Date());
  }

  const isoParsed = parseISO(value);
  if (isValid(isoParsed)) {
    return isoParsed;
  }

  return new Date(value);
};

export const numberFormatter = ({
  value,
  exceptionOnInvalidCast,
  defaultValue,
  locale,
  maximumFractionDigits = 2,
  factor = 1,
}: NumberFormatterProps) => {
  if (!value && value !== 0) return defaultValue;
  const numValue = Number(value);
  if (Number.isNaN(numValue)) {
    if (exceptionOnInvalidCast)
      throw new Error(`'${value}' no se puede convertir a un valor numérico`);
    else return defaultValue;
  }
  return new Intl.NumberFormat(locale.code, { maximumFractionDigits }).format(numValue * factor);
};

export const percentFormatter = ({
  value,
  defaultValue,
  maximumFractionDigits = 2,
  ...rest
}: PercentFormatterProps) => {
  if (!value && value !== 0) return defaultValue;
  const numValue = numberFormatter({
    value,
    defaultValue,
    maximumFractionDigits,
    factor: 100,
    ...rest,
  });
  return `${numValue}%`;
};

export const dateFormatter = ({
  value,
  locale,
  format: formatDate = DateFormat.date,
  defaultValue = '',
}: DateFormatterProps) => {
  if (!value) return defaultValue;

  let dateValue = value;
  if (typeof value === 'string') {
    const parsed = parseDateFromString(value, formatDate);
    if (isValid(parsed)) {
      dateValue = parsed;
    } else {
      console.warn(`dateFormatter recibió un string inválido: ${value}`);
      return defaultValue;
    }
  }

  if (!isValid(dateValue)) {
    console.error('dateFormatter Error: Valor inválido', value);
    return defaultValue;
  }

  try {
    return format(dateValue as Date, formatDate, { locale });
  } catch (error) {
    console.error('dateFormatter Error al formatear:', error);
    return defaultValue;
  }
};

export const booleanFormatter = ({
  value,
  textFalse,
  textTrue,
  defaultValue = '',
}: BooleanFormatterProps) => {
  if (value === null || value === undefined) return defaultValue;
  return value ? textTrue : textFalse;
};

export const defaultFormatters = {
  date: (value: unknown, locale: Locale, defaultValue = '') =>
    dateFormatter({
      value,
      locale,
      defaultValue,
      exceptionOnInvalidCast: true,
      format: DateFormat.date,
    }),
  dateTime: (value: unknown, locale: Locale, defaultValue = '') =>
    dateFormatter({
      value,
      locale,
      defaultValue,
      exceptionOnInvalidCast: true,
      format: DateFormat.dateTime,
    }),
  percent: (value: unknown, locale: Locale, defaultValue = '') =>
    percentFormatter({
      value,
      locale,
      defaultValue,
      maximumFractionDigits: 2,
      exceptionOnInvalidCast: true,
    }),
  number: (value: unknown, locale: Locale, defaultValue = '') =>
    numberFormatter({
      value,
      locale,
      defaultValue,
      maximumFractionDigits: 2,
      exceptionOnInvalidCast: true,
    }),

  boolean: (value: unknown, defaultValue = '') =>
    booleanFormatter({
      value,
      defaultValue,
      textFalse: t('common.no'),
      textTrue: t('common.yes'),
    }),
};
